import React, { useState, useEffect, useContext } from 'react';
import swal from 'sweetalert';
import {
  Box,
  Tab,
  Tabs,
  Card,
  Table,
  Dialog,
  Button,
  Switch,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  CardContent,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControlLabel,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { permitsList, swalMessages } from '../../constants';
import { Context } from '../../context/Contex';
import { getDepartamentos } from '../../services/leadDepartamentosService';
import {
  getDistribucionGlobal,
  putDistribucionGlobal,
  getDistribucionOverride,
  postDistribucionOverride,
  putDistribucionOverride,
  deleteDistribucionOverride,
  getVehiculosActivos,
} from '../../services/distribucionService';

// Categorias en el mismo orden que el motor de distribucion clasifica.
const CATEGORIAS = [
  { key: 'TVS', label: 'TVS' },
  { key: 'ELECTRIC', label: 'Electric' },
  { key: 'VICTORY', label: 'Victory +' },
];

const initialOverride = {
  id: null,
  departamento: '',
  id_vehiculo: '',
  vehiculo: '',
  porcentaje_ia: '',
  estado: 1,
};

const DistribucionContainer = () => {
  const { permisos } = useContext(Context);
  const permitValue = permisos[permitsList.LEADS_ADMIN];

  const [tab, setTab] = useState(0);
  const [globales, setGlobales] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);

  const [openForm, setOpenForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(initialOverride);

  const activeCat = CATEGORIAS[tab].key;

  // ---------- fetch ----------
  const fetchGlobales = async () => {
    const res = await getDistribucionGlobal();
    if (res && res.success) {
      const data = CATEGORIAS.map((c) => {
        const found = (res.data || []).find((g) => g.categoria === c.key);
        return found || { categoria: c.key, porcentaje_ia: 0 };
      });
      setGlobales(data);
    }
  };

  const fetchOverrides = async () => {
    const res = await getDistribucionOverride();
    if (res && res.success) setOverrides(res.data || []);
  };

  const fetchDepartamentos = async () => {
    const res = await getDepartamentos();
    if (res && res.success) setDepartamentos(res.data || []);
  };

  const fetchVehiculos = async () => {
    const res = await getVehiculosActivos();
    if (res && res.success) setVehiculos(res.data || []);
  };

  useEffect(() => {
    fetchGlobales();
    fetchOverrides();
    fetchDepartamentos();
    fetchVehiculos();
  }, []);

  // ---------- derivados por categoria activa ----------
  const globalActivo = globales.find((g) => g.categoria === activeCat) || { categoria: activeCat, porcentaje_ia: 0 };
  const overridesCat = overrides.filter((o) => o.categoria === activeCat);
  const vehiculosCat = vehiculos.filter((v) => v.categoria === activeCat);

  // ---------- globales ----------
  const onChangeGlobal = (value) => {
    setGlobales(globales.map((g) => (g.categoria === activeCat ? { ...g, porcentaje_ia: value } : g)));
  };

  const onSaveGlobal = async () => {
    const valor = Number(globalActivo.porcentaje_ia);
    if (Number.isNaN(valor) || valor < 0 || valor > 100) {
      swal({ title: 'Valor inválido', text: 'El porcentaje debe estar entre 0 y 100', icon: 'warning' });
      return;
    }
    const res = await putDistribucionGlobal({ categoria: activeCat, porcentaje_ia: valor });
    if (res && res.success) {
      swal(swalMessages.SUCCESSFUL_MESSAGE);
      fetchGlobales();
    } else {
      swal(swalMessages.GENERAL_ERROR);
    }
  };

  // ---------- overrides ----------
  const onOpenCreate = () => {
    setForm(initialOverride);
    setIsEdit(false);
    setOpenForm(true);
  };

  const onOpenEdit = (row) => {
    setForm({
      id: row.id,
      departamento: row.departamento,
      id_vehiculo: row.id_vehiculo,
      vehiculo: row.vehiculo,
      porcentaje_ia: row.porcentaje_ia,
      estado: Number(row.estado),
    });
    setIsEdit(true);
    setOpenForm(true);
  };

  const onCloseForm = () => {
    setOpenForm(false);
    setIsEdit(false);
    setForm(initialOverride);
  };

  const onSubmitOverride = async (e) => {
    e.preventDefault();
    const valor = Number(form.porcentaje_ia);
    if (!form.departamento || !form.id_vehiculo) {
      swal({ title: 'Datos incompletos', text: 'Seleccione departamento y vehículo', icon: 'warning' });
      return;
    }
    if (Number.isNaN(valor) || valor < 0 || valor > 100) {
      swal({ title: 'Valor inválido', text: 'El porcentaje debe estar entre 0 y 100', icon: 'warning' });
      return;
    }
    setOpenForm(false);
    const fn = isEdit ? putDistribucionOverride : postDistribucionOverride;
    const res = await fn({ ...form, porcentaje_ia: valor });
    if (res && res.success) {
      swal(swalMessages.SUCCESSFUL_MESSAGE);
      fetchOverrides();
    } else {
      swal(swalMessages.GENERAL_ERROR);
    }
    onCloseForm();
  };

  const onDeleteOverride = (row) => {
    swal(swalMessages.DELETE_CONFIRM).then(async (willDelete) => {
      if (willDelete) {
        const res = await deleteDistribucionOverride(row.id);
        if (res && res.success) {
          setOverrides(overrides.filter((o) => o.id !== row.id));
        } else {
          swal(swalMessages.GENERAL_ERROR);
        }
      }
    });
  };

  return (
    <Box style={{ padding: 16 }}>
      <Typography variant="h5" style={{ marginBottom: 16 }}>
        Configuración de distribución de leads
      </Typography>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        style={{ marginBottom: 16 }}
      >
        {CATEGORIAS.map((c) => <Tab key={c.key} label={c.label} />)}
      </Tabs>

      {/* ----- Global de la categoria activa ----- */}
      <Card style={{ marginBottom: 24 }}>
        <CardContent>
          <Typography variant="h6">{`Porcentaje global a IA — ${CATEGORIAS[tab].label}`}</Typography>
          <Typography variant="body2" style={{ color: '#888', marginBottom: 12 }}>
            % de leads enviados a la IA; el resto va directo a Impulsa.
          </Typography>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <TextField
              type="number"
              variant="outlined"
              size="small"
              value={globalActivo.porcentaje_ia}
              onChange={(ev) => onChangeGlobal(ev.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              disabled={!permitValue}
            />
            <Button color="primary" variant="contained" disabled={!permitValue} onClick={onSaveGlobal}>
              Guardar
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ----- Overrides de la categoria activa ----- */}
      <Card>
        <CardContent>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{`Overrides — ${CATEGORIAS[tab].label}`}</Typography>
            <Button color="primary" variant="contained" disabled={!permitValue} onClick={onOpenCreate}>
              Agregar
            </Button>
          </Box>
          <Typography variant="body2" style={{ color: '#888', marginBottom: 12 }}>
            Un override reemplaza el porcentaje global para el departamento y vehículo indicados.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Departamento</TableCell>
                <TableCell>Vehículo</TableCell>
                <TableCell>% a IA</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overridesCat.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.departamento}</TableCell>
                  <TableCell>{row.vehiculo}</TableCell>
                  <TableCell>{row.porcentaje_ia}</TableCell>
                  <TableCell>{Number(row.estado) === 1 ? 'Activo' : 'Inactivo'}</TableCell>
                  <TableCell>
                    <Button size="small" color="primary" disabled={!permitValue} onClick={() => onOpenEdit(row)}>
                      Editar
                    </Button>
                    <Button size="small" color="secondary" disabled={!permitValue} onClick={() => onDeleteOverride(row)}>
                      Eliminar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {overridesCat.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">{`Sin overrides en ${CATEGORIAS[tab].label}`}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ----- Dialog override (vehiculos filtrados a la categoria activa) ----- */}
      <Dialog open={openForm} onClose={onCloseForm} fullWidth maxWidth="sm">
        <form onSubmit={onSubmitOverride}>
          <DialogTitle>{`${isEdit ? 'Editar' : 'Nuevo'} override — ${CATEGORIAS[tab].label}`}</DialogTitle>
          <DialogContent>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
              <Autocomplete
                noOptionsText="Sin opciones"
                options={departamentos}
                getOptionLabel={(option) => option.NOMBRE_DPT || ''}
                value={departamentos.find((d) => d.NOMBRE_DPT === form.departamento) || null}
                onChange={(e, newVal) => setForm({ ...form, departamento: newVal ? newVal.NOMBRE_DPT : '' })}
                renderInput={(params) => <TextField {...params} label="Departamento" variant="outlined" required />}
              />
              <Autocomplete
                noOptionsText="Sin opciones"
                options={vehiculosCat}
                getOptionLabel={(option) => option.linea || ''}
                value={vehiculosCat.find((v) => String(v.id) === String(form.id_vehiculo)) || null}
                onChange={(e, newVal) => setForm({
                  ...form,
                  id_vehiculo: newVal ? newVal.id : '',
                  vehiculo: newVal ? newVal.linea : '',
                })}
                renderInput={(params) => <TextField {...params} label={`Vehículo (${CATEGORIAS[tab].label}, activos)`} variant="outlined" required />}
              />
              <TextField
                type="number"
                label="% a IA"
                variant="outlined"
                value={form.porcentaje_ia}
                onChange={(ev) => setForm({ ...form, porcentaje_ia: ev.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                required
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={Number(form.estado) === 1}
                    onChange={(ev) => setForm({ ...form, estado: ev.target.checked ? 1 : 0 })}
                  />
                )}
                label="Activo"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCloseForm}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">Guardar</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default DistribucionContainer;
